import { Fragment, Dispatch, useMemo, useState, useRef } from "react"
import { useReactToPrint } from "react-to-print"
import { Link } from "react-router-dom"
import { useDispatch } from "react-redux"

import moment from "moment"
import { Order, OrderDetail } from "../../services/order.service"
import { Setting } from "../../services/user.service"
import { onUpdateOrderStatus } from "../../slices/thunk"
import { currencyFormatter } from "../../helpers/formatter"
import Modal from "../../Common/Components/Modal"
import PrintOrder from "../../Common/Components/Print/PrintOrder"
import { OrderStatusHelper } from "../../Common/Components/Status"
import TableContainer from "../../Common/TableContainer"


type OrderProductModalProps = {
  isOpen: boolean
  setIsOpen: Dispatch<boolean>
  order: Order | undefined
  setOrder: Dispatch<Order>
  orderStatusList: Setting[]
}

const OrderProductModal = (
  { isOpen, setIsOpen, order, setOrder, orderStatusList }: OrderProductModalProps
) => {
  const dispatch = useDispatch<any>();
  const printComponentRef = useRef(null);

  const [orderDetail, setOrderDetail] = useState<OrderDetail | undefined>();

  const orderDetails = order?.orderDetails || []
  const currentStatus = orderStatusList.find((status) => status.code === order?.status)
  const nextStatusButtons = orderStatusList.filter((status) => status.level === (currentStatus?.level || 0) + 1)

  const handleUpdateOrderStatus = (status: string) => {
    if (order?.orderId) {
      dispatch(onUpdateOrderStatus({ orderId: order.orderId, status })).then((response: any) => {
        setOrder(response.payload)
      })
    }
  }

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });

  const columns = useMemo(() => [
    {
      header: "Product Code",
      accessorKey: "product.code",
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <span>
            {cell.row.original.product?.code}
          </span>
        )
      },
    },
    {
      header: "Product Name",
      accessorKey: "product.name",
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <span>
            {cell.row.original.product?.name}
          </span>
        )
      },
    },
    {
      header: "Specification",
      accessorKey: "product.specification",
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <Fragment>
            {cell.row.original.product?.specification?.map((spec: any, index: number) => {
              return (
                spec.key &&
                <span key={index} className="block">
                  <span className="font-semibold">{spec.key}:</span> {spec.value}
                </span>
              )
            })}
          </Fragment>
        )
      },
    },
    {
      header: "Variants",
      accessorKey: "product.cartConfigs",
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <span>
            {cell.row.original.product?.cartConfigs?.map((config: any, index: number) => {
              return (
                config.key &&
                <span key={index} className="block">
                  <span className="font-semibold">{config.key}:</span> {config.value}
                </span>
              )
            })}
          </span>
        )
      },
    },
    {
      header: "Quantity",
      accessorKey: "quantity",
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <span>
            {cell.row.original.quantity}
          </span>
        )
      },
    },
    {
      header: "Price",
      accessorKey: "subtotal",
      enableColumnFilter: false,
      cell: (cell: any) => {
        const subtotal = cell.row.original.subtotal - cell.row.original.discountPrice + cell.row.original.fixedFee
        return (
          <span>
            {currencyFormatter(subtotal)}
          </span>
        )
      },
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: (cell: any) => {
        return (
          <Link to="#!" type="button"
            className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
            onClick={() => {
              setOrderDetail(cell.row.original)
              setTimeout(() => { // To allow order details refresh first
                handlePrint()
              }, 100)
            }}
          >
            <span className="align-middle">Print</span>
          </Link>
        )
      },
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []
  );

  return (
    <Fragment>
      <Modal show={!!isOpen} onHide={() => setIsOpen(false)} modal-center="true"
        className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
        dialogClassName="w-screen md:w-[90rem] bg-white shadow rounded-md dark:bg-zink-600">
        <Modal.Header className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-400 hover:text-red-500">
          <Modal.Title className="text-16">{'Order Details'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <PrintOrder order={order} orderDetail={orderDetail} ref={printComponentRef} />
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-12">
            <div className="xl:col-span-6">
              <label htmlFor="orderDateInput" className="inline-block mb-2 text-base font-medium">Order Date: {moment(order?.createdAt).format('DD MMM yyyy')}</label>
            </div>
            <div className="xl:col-span-6">
              <label htmlFor="deliveryDateInput" className="inline-block mb-2 text-base font-medium">Delivery Date: {order?.deliveryDate ? moment(order?.deliveryDate).format('DD MMM yyyy') : '-'}</label>
            </div>
            <div className="xl:col-span-6">
              <label htmlFor="deliveryDateInput" className="inline-block mb-2 text-base font-medium">Delivery Method: {order?.deliveryMethod}</label>
            </div>
            {
              order?.deliveryMethod === 'delivery' &&
              <div className="xl:col-span-6">
                <label htmlFor="deliveryDateInput" className="inline-block mb-2 text-base font-medium">Delivery Address: {order?.deliveryAddress}</label>
              </div>
            }
            <div className="xl:col-span-12">
              <label htmlFor="customerNameInput" className="inline-block mb-2 text-base font-medium">Customer Name: {order?.user?.name}</label>
            </div>
            <div className="xl:col-span-12">
              <label htmlFor="amountInput" className="inline-block mb-2 text-base font-medium">Total: {currencyFormatter((order?.total || 0))}</label>
            </div>
            <div className="xl:col-span-12">
              <label htmlFor="deliveryStatusSelect" className="inline-block mb-2 text-base font-medium mr-2">Status:</label>
              <OrderStatusHelper status={order?.status || ''} list={orderStatusList} />
            </div>
            <div className="xl:col-span-12">
              {orderDetails && orderDetails.length > 0 ?
                <TableContainer
                  isPagination={false}
                  columns={(columns || [])}
                  data={(orderDetails || [])}
                  customPageSize={10}
                  divclassName="mt-5 overflow-x-auto"
                  tableclassName="w-full whitespace-nowrap"
                  theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                  thclassName="px-3.5 py-2.5 font-semibold text-slate-500 border-b border-slate-200 dark:border-zink-500 dark:text-zink-200"
                  tdclassName="px-3.5 py-2.5 border-y border-slate-200 dark:border-zink-500"
                  PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
                />
                :
                (<div className="noresult">
                  <div className="py-6 text-center">
                    <h5 className="mt-2 mb-1">Sorry! No Result Found</h5>
                  </div>
                </div>)
              }
            </div>
            <div className="xl:col-span-12">
              <div className="rtl:lg:text-left">
                {
                  !currentStatus?.isFinal && nextStatusButtons?.map((status: Setting, index: number) => {
                    return (
                      <Link to="#!" type="button" key={index}
                        className="ml-4 text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20"
                        onClick={() => handleUpdateOrderStatus(status.code)}
                      >
                        <span className="align-middle">{status.value}</span>
                      </Link>
                    )
                  })
                }
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Fragment>
  )
}

export default OrderProductModal;